<script setup lang="ts">
    import { computed, onMounted, onUnmounted, ref } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useTime } from 'composables/src/useTime'

    const props = withDefaults(
        defineProps<{
            date: Date | string | number
            endDate?: Date | string | number
            autoUpdate?: boolean
            updateInterval?: number
        }>(),
        {
            endDate: undefined,
            autoUpdate: true,
            updateInterval: 1000,
        },
    )

    const { t: $t } = useI18n({
        useScope: 'global',
    })

    const now = ref(Date.now())
    let intervalId: ReturnType<typeof setInterval> | null = null

    const targetDate = computed(() => {
        if (props.date instanceof Date) {
            return props.date.getTime()
        }
        if (typeof props.date === 'string') {
            return new Date(props.date).getTime()
        }
        return props.date
    })

    const targetEndDate = computed(() => {
        if (props.endDate === undefined) {
            return null
        }
        if (props.endDate instanceof Date) {
            return props.endDate.getTime()
        }
        if (typeof props.endDate === 'string') {
            return new Date(props.endDate).getTime()
        }
        return props.endDate
    })

    const deltaMs = computed(() => {
        const reference = targetEndDate.value ?? now.value
        return targetDate.value - reference
    })

    const isInFuture = computed(() => {
        return deltaMs.value > 0
    })

    const absoluteDelta = computed(() => {
        return Math.abs(deltaMs.value)
    })

    const { formatDelta } = useTime()
    const formattedTime = computed(() => {
        const delta = absoluteDelta.value
        return formatDelta(delta)
    })

    const displayText = computed(() => {
        const { value, unit } = formattedTime.value

        if (props.endDate) {
            return `${value} ${unit}`
        }

        if (isInFuture.value) {
            return `${$t('time.future')} ${value} ${unit}`
        }

        return `${value} ${unit} ${$t('time.past')}`
    })

    const dateTimeAttribute = computed(() => {
        return new Date(targetDate.value).toISOString()
    })

    const updateNow = () => {
        now.value = Date.now()
    }

    onMounted(() => {
        if (props.autoUpdate && !props.endDate) {
            intervalId = setInterval(updateNow, props.updateInterval)
        }
    })

    onUnmounted(() => {
        if (intervalId) {
            clearInterval(intervalId)
        }
    })
</script>

<template>
    <time :datetime="dateTimeAttribute">
        {{ displayText }}
    </time>
</template>
